import { Credential } from 'admin/credentials/Credentials';
import { Label } from 'admin/labels/Label.types';
import { Location } from 'admin/locations/Locations';
import { ModuleId } from 'admin/modules/Modules';
import { Role } from 'admin/roles/Roles';
import { Source } from 'admin/sources/Sources';
import { User } from 'admin/users/Users';
import { Queue } from 'common/customers/conversation-panel/conversation-metadata/ConversationMetaData.types';
import { Customer } from 'common/customers/Customers';
import { Operation } from 'common/util/ApiCommon';
import { BatchAggregateRequest } from 'core/util/Aggregate';
import { ResponseCode } from 'core/util/Response';
import { SearchRequest } from 'core/util/Search';
import { RealTimeMessageType } from 'inbox/Inbox.types';

export type AssignCommentArgs = {
    id: number;
    assigneeType: AssigneeType;
} | null;

export interface GetCommentsArgs {
    includeSource?: boolean;
    includeCustomer?: boolean;
    includeLocation?: boolean;
    includeAssignee?: boolean;
    includePoster?: boolean;
    includePage?: boolean;
    includeComplianceConfig?: boolean;
    includeTemplate?: boolean;
    includeLabels?: boolean;
    includeCredentials?: boolean;
    includeApproverIds?: boolean;
    includeAttributionUsers?: boolean;
    getConversation?: boolean;
    moduleIds?: string[];
    injectSourcesFilter?: boolean;
    getCredentialStatusForLocations?: boolean;
    sanitizeText?: boolean;
}

export interface BatchAggregateArgs {
    type: Type;
    moduleIds: ModuleId[];
}

export interface GetAncestorsArgs extends GetCommentsArgs {
    type: string;
}

export interface RemoveCommentsArgs {
    privateNote?: boolean;
}

export interface PollCommentsArgs {
    channelId: string | undefined;
    moduleIds?: string[];
    type?: Type;
}

export enum FieldName {
    Id = 'comment2.id',
    TenantId = 'comment2.tenantID',
    EntityId = 'comment2.entityID',
    SourceUid = 'comment2.sourceUid',
    Type = 'comment2.type',
    Date = 'comment2.date',
    Labels = 'comment2.labels',
    AllLabels = 'comment2.allLabels',
    Text = 'comment2.text',
    ParentId = 'comment2.parentID',
    QueueId = 'comment2.queueID',
    StageId = 'comment2.stageID',
    CustomerId = 'comment2.customerID',
    AssignedTo = 'comment2.assignedTo',
    UpdatedDate = 'comment2.updatedDate',
    QueryString = '_query_string_', // elastic query
    Assignee = 'comment2.assignee',
    AssgineeId = 'comment2.assignee.id',
    AssigneeType = 'comment2.assignee.type',
    AssignedToUserId = 'comment2.assignedToUserID',
    AssignedToRoleId = 'comment2.assignedToRoleID',
    ConversationId = 'comment2.conversationID',
    DueDate = 'conversation.dueDate',
    UserType = 'comment2.posterUserType',
    Sentiment = 'comment2.sentiment',
    CategoryId = 'comment2.category.id',
    CategorySentiment = 'comment2.category.sentiment',
    CommentType = 'conversation.commentType',
    Medias = 'comment2.medias',
    ApprovalStatus = 'comment2.approver.approvalStatus',
    Properties = 'comment2.properties',
    CommentHistory = 'commentHistory.commentID',
    NormalizedRating = 'comment2.properties._1.rating.normalizedRatingValue',
}

export enum FQFieldName {
    Type = 'R4EComment2.comment2.type',
    CustomerID = 'R4EComment2.comment2.customerID',
    AssignedToUserId = 'R4EComment2.comment2.assignedToUserID',
    AssignedToRoleId = 'R4EComment2.comment2.assignedToRoleID',
    AssignedToQueueId = 'R4EComment2.comment2.assignedToQueueID',
    ApprovalStatus = 'R4EComment2.conversation.approver.approvalStatus',
    ApprovalStatusComment = 'R4EComment2.comment2.approver.approvalStatus',
    CreatedDate = 'R4EComment2.comment2.createdDate',
    Date = 'R4EComment2.comment2.date',
    DueDate = 'R4EComment2.comment2.dueDate',
    ResponseStatus = 'R4EComment2.comment2.responseStatus',
    Sentiment = 'R4EComment2.comment2.sentiment',
    Emotions = 'R4EComment2.comment2.emotions',
    CategoryId = 'R4EComment2.comment2.categoryID',
    Labels = 'R4EComment2.comment2.labels',
    Id = 'R4EComment2.comment2.id',
}

export enum SortName {
    Id = 'comment2.id',
    Date = 'comment2.date',
    DueDate = 'conversation.dueDate',
    CreatedDate = 'conversation.createdDate',
    UpdatedDate = 'comment2.updatedDate',
    HistoryCreatedDate = 'commentHistory.createdDate',
}

export enum CommentType {
    ReviewConversation = 2,
    Survey = 3,
    SurveyReply = 4,
    SurveyConversation = 5,
    SocialPost = 6,
    SocialPostReply = 7,
    SocialPostConversation = 8,
    Message = 9,
    MessageConversation = 10,
    SocialListening = 11,
    Review = 15,
    ReviewReply = 16,
    Mention = 17,
    MentionReply = 18,
    SocialAds = 19,
}

export enum ParticipantType {
    Watcher = 0,
    Mentioned = 1,
    Owner = 2,
}

export enum UserType {
    User = 1,
    Business = 2,
    Customer = 3,
}

export enum MediaType {
    Link = 1,
    Photo = 2,
    Video = 3,
    NotMapped = 4,
    Sticker = 5,
    Audio = 6,
}

export enum MediaMimeType {
    Pdf = 'application/pdf',
}

export enum PublishStatus {
    Success = 0,
    Queued = 1,
    Duplicate = 2,
    Pending = 3,
    PendingManual = 4,
    Posted = 5,
    AuthorizationInvalid = 1000,
    AuthorizationRevoked = 1001,
    AuthorizationExpired = 1002,
    AuthorizationRejected = 1003,
    InsufficientPermission = 1004,
    DataValidationError = 1005,
    DuplicateError = 1006,
    LocationSetupError = 1007,
    CredentialNotFound = 1008,
    RejectedContent = 1009,
    MediaResourceError = 1010,
    MediaRejectedContent = 1011,
    TemplatingError = 1012,
    TenantModuleAccessMissing = 1013,
    SchedulingError = 1014,
    PhoneNumberBlocked = 1015,
    PhoneNumberInvalid = 1016,
    AccountBlocked = 1017,
    BadRequest = 1018,
    NotFound = 1019,
    ObjectDeleted = 1020,
    Unknown = 2000,
    InternalServerError = 2001,
    ServiceUnavailable = 2002,
    GatewayTimeout = 2003,
    ThirdPartyApiDown = 2004,
    PublishServiceError = 2005,
    AppRateLimitExceeded = 2006,
    UserRateLimitExceeded = 2007,
    MediaProcessingError = 2008,
    Profanity = 10000,
}

export enum CommentProperty {
    Rating = '_1',
    NPS = '_2',
    Title = '_3',
    SyndicatedSourceUid = '_4',
    SourceDate = '_5',
    Language = '_6',
    TemplateId = '_7',
    RTU = '_8',
    RequestId = '_10',
    SentDate = '_11',
    PrivateNote = '_12',
    PrivateResponse = '_13',
}

export enum DataType {
    Long = 0,
    String = 1,
    Date = 2,
    Double = 3,
    Boolean = 4,
    Email = 5,
    Rating = 6,
    Nps = 7,
    Encrypted = 8,
}

export enum SystemLabel {
    Read = 1,
    Unread = -1,
    Profanity = 2,
    NotProfanity = -2,
    Published = 3,
    Unpublished = -3,
    Shareable = 4,
    NotShareable = -4,
    Syndicated = 5,
    NotSyndicated = -5,
    Responded = 6,
    Unresponded = -6,
    Attributed = 7,
    NotAttributed = -7,
    Boosted = 8,
    NotBoosted = -8,
    Open = 9,
    Closed = -9,
    Overdue = 10,
    NotOverdue = -10,
    Flagged = 11,
    Unflagged = -11,
    Expired = 12,
    NotExpired = -12,
    WontRespond = -13,
    Locked = 15,
    Unlocked = -15,
    Bot = 16,
    PrivateNote = 20,
    NotRespondable = -21,
    NeedsResponse = 32,
    NoResponseRequired = 33,
    Positive = 1000,
    Neutral = 1001,
    Negative = 1002,
    Warn = 18,
    Block = 19,
    PendingApproval = 22,
    Webchat = 23,
    ReviewRequest = 24,
    Updated = 26,
    Suspect = 28,
    Genuine = 29,
}

export interface Media {
    name?: string;
    mediaType: MediaType;
    mimeType?: string;
    title?: string;
    message?: string;
    shortMessage?: string;
    url: string;
    targetLink?: string;
    properties?: { string: string };
    system?: boolean;
    attachmentType?: string;
    thumbnailUrl?: string;
}

interface Poster {
    id: string;
    userType: UserType;
    name?: string;
    profileUrl?: string;
    photoUrl?: string;
    initials?: string;
    color?: string;
    image?: string;
}

interface Property {
    name: string;
    dataType: DataType;
    stringValues?: string[];
    stringValue?: string;
    longValues?: number[];
    longValue?: number;
    doubleValues?: number[];
    doubleValue?: number;
    dateValues?: number[];
    dateValue?: number;
    encryptedValues?: string[];
    encryptedValue?: string;
    emailValues?: string[];
    emailValue?: string;
    booleanValues?: boolean[];
    booleanValue?: boolean;
    order?: number;
    system?: boolean;
    rating?: Rating;
}

interface Participant {
    userID: number;
    participantType: ParticipantType;
}

export enum Type {
    Comment = 0,
    Conversation = 1,
}

interface SubRating {
    id: number;
    name: string;
    ratingValue: number;
    normalizedRating: number;
    text: string;
}

export interface Rating {
    ratingValue: number;
    normalizedRating: number;
    subRatings?: SubRating[];
    recommended?: boolean;
    reviewProperties?: any[];
}

export interface Category {
    categoryID: number;
    sentiment: Sentiment;
    percentage?: number;
}

export enum Sentiment {
    Negative = -1,
    Neutral = 0,
    Positive = 1,
    None = -2,
}

export interface Emotion {
    Anger: number;
    Fear: number;
    Disgust: number;
    Joy: number;
    Anticipation: number;
    Suprise: number;
    Sadness: number;
    Trust: number;
}

interface ConversationMetadata {
    stageID?: string;
    rootCauseID?: string;
    closingReasonID?: string;
    otherText?: string;
}

export enum ResponseFailedReason {
    ClientRequest = 1,
    DidNotPostForOtherReason = 2,
    CredentialsMissing = 3,
    CredentialsInvalid = 4,
    ReviewDeleted = 5,
    UnableToPostForOtherReason = 6,
    Other = 7,
}

export interface Comment {
    id: string;
    createdDate?: number;
    createdByID?: number;
    updatedDate?: number;
    updatedByID?: number;
    agencyID: number;
    tenantID: number;
    entityID: number;
    sourceUid: number;
    sourceID?: string;
    customerID: string;
    commentType: CommentType;
    date: number;
    templateID?: string;
    complete?: boolean;
    url?: string;
    externalID?: string;
    numChildren?: number;
    text?: string;
    medias?: Media[] | null;
    poster?: Poster;
    labels?: SystemLabel[];
    parentID?: string;
    removed?: boolean;
    properties?: { [keyof: string]: Property };
    unresponded?: number;
    hash?: number;
    queueID?: string;
    stageID?: string;
    assignedTo?: number;
    assignee?: Assignee;
    participants?: Participant[];
    dueDate?: number;
    closedDate?: number;
    publishStatus?: PublishStatus;
    requestTemplateID?: string;
    responseTime?: number;
    closeTime?: number;
    expiresAt?: number;
    newDate?: boolean; // ui
    customer?: Customer; // ui
    source?: Source; // ui
    location?: Location; // ui
    assignedUser?: User; // ui
    assigneeObject?: User | Role | Queue; // ui
    new?: boolean; // ui
    page?: any; // ui
    editing?: boolean; // ui
    conversationID?: string;
    type: Type;
    conversationLockDate?: number;
    mentionsData?: MentionsData;
    commentId?: string;
    categories?: Category[];
    title?: string;
    approver?: Approver;
    sentiment?: Sentiment;
    emotions?: Emotion;
    template?: any; // ui
    categoryLabels?: Label[]; // ui
    credentials?: Credential[]; // ui
    conversationMetadata?: ConversationMetadata;
    replyToID?: string;
    replies?: Comment[];
    generatedID?: string;
    attributions?: Attribution[];
    textLanguage?: Locale;
    translatedText?: string;
    responseFailedReason?: ResponseFailedReason;
    responseFailedReasonExplanation?: string;
    credentialStatuses?: any;
    recommendedReplies?: any[];
    translatedTitle?: string;
}

export enum Locale {
    English = 'en',
}

export interface Attribution {
    attributionType: AttributionType;
    userID: number;
    visible?: boolean;
    user?: User; // ui
}

export enum AttributionType {
    Manual = 1,
    Requestor,
    Mention,
}

export interface MentionsData {
    pageID: string;
    pageName: string;
    mentions: Mention[];
}

export interface Mention {
    id: string;
    name: string;
    permalink: string;
    offset: number;
    length: number;
}

export interface Comment2Props {
    comment2: Comment;
}

export interface PayloadMessage {
    type: RealTimeMessageType;
    id: string;
    comment?: Comment;
    presenceStatus?: number;
    commentType?: string;
}

export interface Conversation {
    comment: Comment;
    customer?: Customer;
    operation?: Operation;
    manualPublish?: boolean;
}

export enum Reaction {
    LIKE = 1,
    UNDOLIKE = -1,
    PRAISE = 2,
    UNDOPRAISE = -2,
    MAYBE = 3,
    UNDOMAYBE = -3,
    EMPATHY = 4,
    UNDOEMPATHY = -4,
    INTEREST = 5,
    UNDOINTEREST = -5,
    APPRECIATION = 6,
    UNDOAPPRECIATION = -6,
}

export interface Assignee {
    id: number;
    assigneeType: AssigneeType;
}

export enum AssigneeType {
    User = 0,
    Role = 1,
    Queue = 2,
}

export interface Lock {
    lockedByID: number;
    lockedBy: string;
    lockedTime: number;
    expireTime?: number;
    commentID: string;
    locked: boolean;
}

export const CommentTypeGroup: Record<string, CommentType[]> = {
    Reviews: [CommentType.Review, CommentType.ReviewReply],
    SocialPosts: [CommentType.SocialAds, CommentType.SocialPost, CommentType.SocialPostReply, CommentType.Mention],
    Messages: [CommentType.Message],
    Surveys: [CommentType.Survey, CommentType.SurveyReply],
};

export interface SaveConversationProps {
    tenantID: number;
    message: string;
    customerID: string;
    conversationID?: string;
    locationUid: number;
    agencyID: number;
}

export interface CanPublishArgs {
    userId: number;
    commentType: CommentType;
    entityID: number;
}

export interface Approver {
    ids?: number[];
    approverType?: ApproverType;
    approvalStatus?: ApprovalStatus;
    approvedByUserID?: number;
}

export enum ApproverType {
    User = 1,
    Role = 2,
}

export enum ApprovalStatus {
    PendingApproval = 1,
    Approved = 2,
    Rejected = 3,
    PendingModeration = 4,
    Failed = 5000, // used in bulk-actions ui
}

export enum ConversationStatType {
    Emotions = 1,
    Sentiment = 2,
    Categories = 3,
}

export interface ConversationStatsResponse {
    [key: string]: ConversationStat[];
}

export interface ConversationStat {
    name: string;
    value: number;
    id: number;
    sentiment: Sentiment;
}

export interface BatchAggregateCommentsRequest {
    type: Type;
    ModuleIds: ModuleId[];
    batchAggregateRequest: BatchAggregateRequest;
}

export interface Distribution {
    email?: string;
    userID?: string;
    roleID?: string;
}

export interface ForwardEmailRequest {
    commentID: string;
    params?: Record<string, string>;
    distributions: Distribution[];
    subject: string;
    message: string;
}

export interface SearchCommentHistoriesRequest {
    includeLinkMetadata: boolean;
    searchRequest: SearchRequest;
}

export enum CommonSentiment {
    Positive = 1,
    Negative,
    Neutral,
    None,
}

export const CommentToCommonSentiment = {
    [Sentiment.Positive]: CommonSentiment.Positive,
    [Sentiment.Negative]: CommonSentiment.Negative,
    [Sentiment.Neutral]: CommonSentiment.Neutral,
    [Sentiment.None]: CommonSentiment.None,
};

export const CommonToCommentSentiment = {
    [CommonSentiment.Positive]: Sentiment.Positive,
    [CommonSentiment.Negative]: Sentiment.Negative,
    [CommonSentiment.Neutral]: Sentiment.Neutral,
    [CommonSentiment.None]: Sentiment.None,
};

export interface AttributionRequest {
    commentID: string;
    attribution: Attribution;
}

export interface CommentFamily {
    parent: Comment;
    children: Comment[];
}

export interface ApprovalStatusUpdate {
    commentID: string;
    status: ResponseCode;
}

export interface BulkApprovalStatusUpdateComment {
    commentID: string;
    manualPublish: boolean;
}

export interface BulkApprovalStatusUpdateResponse {
    approvalStatusUpdates: ApprovalStatusUpdate[];
}

export interface BulkApprovalStatusUpdateRequest {
    bulkApprovalStatusUpdateComments: BulkApprovalStatusUpdateComment[];
    approvalStatus: ApprovalStatus;
    approvedByUserID: number;
}

export interface ExportReportDataRequest {
    searchRequest: SearchRequest;
    moduleIds: ModuleId[];
    reportViewID?: string;
    reportType?: string;
}

export enum ExportReportDataFormat {
    Csv = 1,
    Excel = 2,
}
